import type { DrawingData } from "../types";

/** Model Manager's import-status value once a file's tile set is actually ready. */
const COMPLETED_STATUS = "COMPLETED";

/**
 * Whether this drawing's tile set exists and can be fetched.
 *
 * `platform_file_status` alone is the signal: it reports the PDF parsing
 * stage, which is what writes the tiles and previews. `scene_version_status`
 * covers scene building — a later, unrelated stage that applies the
 * transformation matrix — so a drawing can be perfectly renderable in the
 * mini map while the scene build is still running or has failed. Gating on
 * it too (as this did before BM-13878) withheld mini maps that were ready.
 * Confirmed with Platform and Model Manager on the RFD00134 thread.
 *
 * A missing status counts as not ready: on mobile there is no PDF fallback,
 * so guessing "ready" would mean a fetch that can only fail.
 *
 * @remarks
 * Deliberately a leaf module rather than part of `manifestResolver.ts`:
 * `minimapSlice.ts` needs this predicate, and reaching it through the
 * `tiledDrawing` barrel would import `downloadTiledDrawingData.ts`, which
 * imports the slice back — a cycle `npm run skott` fails the build on.
 */
export function hasAvailableTileSet(
    drawingData: Pick<DrawingData, "platformFileInstanceId" | "platformFileStatus">,
): boolean {
    return Boolean(drawingData.platformFileInstanceId) && drawingData.platformFileStatus === COMPLETED_STATUS;
}
